<template>
  <div>
    <form :class="{ 'o-form': true, 'o-form--sidebar-program-search': isOnResultsPage }">
      <fieldset class="o-form__section s-content-block s-content-block--small">
        <h4>Block Transfer Search</h4>
        <div v-if="!isOnResultsPage">
          <div v-html="searchHeaderContent"></div>
        </div>
        <div class="m-field-group">
          <label>From Institution</label>
          <Multiselect
            name="fromInstitution"
            v-model="filter.sndrInstitutionId"
            @input="
              (val) =>
                (filter.sndrInstitutionId = val === 'all' ? 'all' : val ? { id: val.id } : null)
            "
            :options="sendingInstitutions"
            :track-by="'id'"
            :custom-label="sendingInstitutionCustomLabel"
            :placeholder="'Select Institution'"
            :show-labels="false"
            @select="
              (val) =>
                (filter.sndrInstitutionId = val === 'all' ? 'all' : val ? { id: val.id } : null)
            "
            :disabled="!sendingInstitutions || !sendingInstitutions.length"
            :loading="sendingInstitutionsLoading"
          >
            <template #option="props">
              <span :title="sendingInstitutionCustomLabel(props.option)">{{
                sendingInstitutionCustomLabel(props.option)
              }}</span>
            </template>
          </Multiselect>
        </div>
        <div class="m-field-group">
          <label for="">To Institution</label>
          <Multiselect
            v-model="filter.rcvrInstitutionId"
            @input="
              (val) =>
                (filter.rcvrInstitutionId = val === 'all' ? 'all' : val ? { id: val.id } : null)
            "
            :options="receivingInstitutions"
            :track-by="'id'"
            :custom-label="receivingInstitutionCustomLabel"
            :placeholder="'Select Institution'"
            :show-labels="false"
            @select="
              (val) =>
                (filter.rcvrInstitutionId = val === 'all' ? 'all' : val ? { id: val.id } : null)
            "
            :disabled="!receivingInstitutions || !receivingInstitutions.length"
            :loading="receivingInstitutionsLoading"
          >
            <template #option="props">
              <span :title="receivingInstitutionCustomLabel(props.option)">{{
                receivingInstitutionCustomLabel(props.option)
              }}</span>
            </template>
          </Multiselect>
        </div>
        <div class="m-field-group m-field-group--contain">
          <label for="">Academic Year</label>
          <select v-model="filter.year">
            <option :value="null">All Years (2011 onwards)</option>
            <option
              v-for="yearsOption in yearsOptions"
              :key="yearsOption.year"
              :value="yearsOption.year"
            >
              {{ yearsOption.label }}
            </option>
          </select>
        </div>
        <div class="m-field-group m-field-group--contain">
          <label for="">Program</label>
          <input
            v-model="filter.program"
            type="text"
            placeholder="e.g. 'Comm'"
            maxlength="20"
            @input="trimInput"
          />
        </div>
      </fieldset>
      <button type="button" class="a-button" @click="submitSearch">Search</button>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, watch, onMounted } from 'vue'
import axios from 'axios'
import Multiselect from 'vue-multiselect'
import { defineProps, defineEmits, defineExpose } from 'vue'

const blockTransferSearchFilterDataStorageName = 'block-transfer-search-params'

const props = defineProps({
  nonce: { type: String, required: true },
  basePagePath: { type: String, default: false },
  resultsPagePath: { type: String, default: false },
  isOnResultsPage: { type: Boolean, default: false },
  searchHeaderContent: { type: String, required: true },
  apiUrls: { type: Object, required: true },
})

const emit = defineEmits(['onSubmit'])

const filterDataLocalStorage = sessionStorage.getItem(blockTransferSearchFilterDataStorageName)
const filterData = reactive({
  sndrInstitutionId: 'all',
  rcvrInstitutionId: 'all',
  year:
    new Date().getMonth() >= 5
      ? new Date().getFullYear() % 100
      : (new Date().getFullYear() - 1) % 100,
  program: null,
})

if (typeof filterDataLocalStorage === 'string') {
  Object.assign(filterData, JSON.parse(filterDataLocalStorage))
}

const filter = reactive(filterData)
const sendingInstitutions = ref([])
const receivingInstitutions = ref([])
const yearsOptions = ref([])
const sendingInstitutionsLoading = ref(false)
const receivingInstitutionsLoading = ref(false)

const getYearsSelectOptionData = () => {
  const startYear = 2011
  const currentYear =
    new Date().getMonth() >= 5 ? new Date().getFullYear() : new Date().getFullYear() - 1
  let years = []
  for (let i = startYear; i <= currentYear; i++) {
    years.push({ year: i % 100, label: `${i}/` + (i + 1).toString().slice(-2) })
  }
  return years.reverse()
}

const restorePersistence = () => {
  let persistedData = sessionStorage.getItem(blockTransferSearchFilterDataStorageName)
  if (typeof persistedData === 'string') {
    persistedData = JSON.parse(persistedData)
  }
  if (persistedData == null) {
    getSendingInstitutions()
    getReceivingInstitutions()
    yearsOptions.value = getYearsSelectOptionData()
    return
  }
  const rcvrInstitutionId = persistedData.rcvrInstitutionId
  if (rcvrInstitutionId) {
    filter.rcvrInstitutionId = rcvrInstitutionId
    getSendingInstitutions(filter.rcvrInstitutionId)
  } else {
    getSendingInstitutions()
  }
  const sndrInstitutionId = persistedData.sndrInstitutionId
  if (sndrInstitutionId) {
    filter.sndrInstitutionId = sndrInstitutionId
    getReceivingInstitutions(filter.sndrInstitutionId)
  } else {
    getReceivingInstitutions()
  }
}

const updatePersistence = () => {
  sessionStorage.setItem(blockTransferSearchFilterDataStorageName, JSON.stringify(filter))
}

const clearPersistence = () => {
  sessionStorage.removeItem(blockTransferSearchFilterDataStorageName)
}

const newSearch = () => {
  clearPersistence()
  window.location.href = props.basePagePath
}

const submitSearch = () => {
  updatePersistence()
  if (!props.isOnResultsPage) {
    window.location = props.resultsPagePath
    return
  }
  submit()
}

const submit = () => {
  if (filter.program) {
    filter.program = filter.program.trim()
  }
  emit('onSubmit', generateSubmitData(filter))
}

const generateSubmitData = (filter) => {
  const data = {
    sndrInstitutionId: filter.sndrInstitutionId.id,
    rcvrInstitutionId: filter.rcvrInstitutionId.id,
    year: filter.year,
    program: filter.program,
  }

  return data
}

const trimInput = (event) => {
  filter.program = sanitizeInput(event.target.value)
}

const sanitizeInput = (input) => {
  return input.replace(/[\0\x08\x09\x1a\n\r"'\\\%]/g, (char) => {
    switch (char) {
      case '\0':
        return '\\0'
      case '\x08':
        return '\\b'
      case '\x09':
        return '\\t'
      case '\x1a':
        return '\\z'
      case '\n':
        return '\\n'
      case '\r':
        return '\\r'
      case '"':
      case "'":
      case '\\':
      case '%':
        return '\\' + char
    }
  })
}

const sendingInstitutionCustomLabel = (selectedOption) => {
  if (selectedOption === null || selectedOption === undefined) {
    return 'All Institutions'
  }
  const institution =
    typeof selectedOption === 'object'
      ? sendingInstitutions.value.find((institution) => institution.id === selectedOption.id)
      : sendingInstitutions.value.find((institution) => institution.id === selectedOption)
  if (institution && institution.id !== null) {
    return institution.id === 'all'
      ? institution.title
      : institution.code + ' - ' + institution.title
  }
  return 'All Institutions'
}

const receivingInstitutionCustomLabel = (selectedOption) => {
  if (selectedOption === null || selectedOption === undefined) {
    return 'All Institutions'
  }
  const institution =
    typeof selectedOption === 'object'
      ? receivingInstitutions.value.find((institution) => institution.id === selectedOption.id)
      : receivingInstitutions.value.find((institution) => institution.id === selectedOption)
  if (institution && institution.id !== null) {
    return institution.id === 'all'
      ? institution.title
      : institution.code + ' - ' + institution.title
  }
  return 'All Institutions'
}

const getSendingInstitutions = (rcvrInstitutionId = null) => {
  sendingInstitutionsLoading.value = true
  axios
    .get(props.apiUrls.searchBaseURL + 'bt/GetSendingInstitutions', {
      params: { rcvrInstitutionId: rcvrInstitutionId === 'all' ? null : rcvrInstitutionId },
    })
    .then((response) => {
      if (!response.data.find((institution) => institution.id === null)) {
        response.data.unshift({ id: 'all', code: 'All', title: 'All Institutions' })
      }

      sendingInstitutions.value = response.data.sort((a, b) => {
        if (a.id === 'all') return -1
        if (b.id === 'all') return 1
        return a.code.localeCompare(b.code)
      })

      sendingInstitutionsLoading.value = false
    })
    .catch((error) => {
      console.error(error)
      sendingInstitutionsLoading.value = false
    })
}

const getReceivingInstitutions = (sndrInstitutionId = null) => {
  receivingInstitutionsLoading.value = true
  axios
    .get(props.apiUrls.searchBaseURL + 'bt/GetReceivingInstitutions', {
      params: { sndrInstitutionId: sndrInstitutionId === 'all' ? null : sndrInstitutionId },
    })
    .then((response) => {
      if (!response.data.find((institution) => institution.id === null)) {
        response.data.unshift({ id: 'all', code: 'All', title: 'All Institutions' })
      }

      receivingInstitutions.value = response.data.sort((a, b) => {
        if (a.id === 'all') return -1
        if (b.id === 'all') return 1
        return a.code.localeCompare(b.code)
      })

      receivingInstitutionsLoading.value = false
    })
    .catch((error) => {
      console.error(error)
      receivingInstitutionsLoading.value = false
    })
}

watch(
  () => filter,
  () => {
    updatePersistence()
  },
  { deep: true },
)

watch(
  () => filter.sndrInstitutionId,
  () => {
    getReceivingInstitutions(filter.sndrInstitutionId.id)
  },
)

watch(
  () => filter.rcvrInstitutionId,
  () => {
    getSendingInstitutions(filter.rcvrInstitutionId.id)
  },
)

onMounted(() => {
  yearsOptions.value = getYearsSelectOptionData()
  restorePersistence()
  if (props.isOnResultsPage) {
    submit()
  }
})

defineExpose({
  newSearch,
})
</script>
